import { useState } from "react";
import { Box, Flex, Text } from "rebass/styled-components";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { BiErrorCircle } from "react-icons/bi";
import { MdOutlineMovieCreation, MdOutlineTimer } from "react-icons/md";
import Lottie from "lottie-react";
import { Divider } from "MVVM/Components/Divider";
import { Button } from "MVVM/Components/Button";
import { useUser } from "MVVM/Hooks/useUser";
import { MomentTags } from "MVVM/Views/MatchHistory/Components/Shared/MomentTags";
import { TMoment } from "MVVM/Views/MatchHistory/@types";

import Remixing from "../../../../../../../animations/remixing.json";
import { ClipThumbnail } from "../ClipThumbnail";
import { ReactComponent as RemixIcon } from "../../../../../../../assets/images/badges/remix-icon.svg";
import { Loading } from "MVVM/Views/Homepage/Loading";
import { MomentClipViewModel } from "./ViewModel";
import {
  ProcessingButton,
  TransitTimeWrapper,
} from "MVVM/Views/MatchHistory/styledComponents";
import { TransitTime } from "MVVM/Components/TransitTime";
import { usePortal } from "MVVM/Hooks/usePortal";
import { QuickEdit } from "MVVM/Components/QuickEdit";
import { analytics } from "MVVM/Analytics";
import { ModalType } from "MVVM/@types";
import { ClipFullModalState } from "MVVM/State/modals";
import { useSetRecoilState } from "recoil";

const MomentClip = ({
  moment,
  outdated,
}: {
  moment: TMoment;
  outdated?: boolean;
}) => {
  const { clip } = moment;
  const { allstarUser } = useUser();
  const history = useHistory();
  const isPruned = clip.status === "PRUNED";
  const [processing, setProcessing] = useState(false);
  const { createPrunedClip } = MomentClipViewModel();
  const setClipFullModalState = useSetRecoilState(ClipFullModalState);
  const {
    portalOpen: quickEditPortalOpen,
    setPortalOpen: setQuickEditPortalOpen,
    Portal: QuickEditPortal,
  } = usePortal(
    <QuickEdit
      momentId={moment.id}
      closeModal={() => setQuickEditPortalOpen(false)}
    />,
    "root",
  );
  const { portalOpen, setPortalOpen, Portal } = usePortal(
    <TransitTimeWrapper>
      <TransitTime user={allstarUser?.user?._id} />
    </TransitTimeWrapper>,
  );
  const handleClick = async () => {
    setProcessing(true);
    createPrunedClip({
      variables: { clipIdentifier: clip.shareId },
    });
  };

  return (
    <>
      {portalOpen && Portal}
      {quickEditPortalOpen && QuickEditPortal}
      <Divider />
      <Flex flexDirection={["column", "column", "row"]} style={{ gap: 20 }}>
        <Flex
          flexDirection={["column", "column", "row"]}
          alignItems={"start"}
          width="100%"
          style={{ gap: 20 }}
          flexWrap={"wrap"}
        >
          {isPruned ? (
            <PrunedThumbnail
              style={{
                backgroundImage: `url(${clip?.thumbnailUrl})`,
              }}
            >
              <DarkOverlay />
              <StyledErrorIcon />
            </PrunedThumbnail>
          ) : (
            <ClipThumbnail clip={clip} />
          )}

          <Flex
            flexDirection={"column"}
            alignContent={"space-between"}
            flexGrow={1}
            style={{ gap: 16 }}
          >
            <Flex
              flexDirection={["column", "row"]}
              justifyContent={"space-between"}
              width="100%"
            >
              <Flex
                flexDirection={"column"}
                style={{ gap: 4 }}
                alignItems={"flex-start"}
              >
                {isPruned ? (
                  <Text variant={"text.labelSmall"} color="ice">
                    This Clip Was Archived
                  </Text>
                ) : (
                  <StyledClipTitle
                    variant={"text.labelSmall"}
                    onClick={() => {
                      if (clip?.shareId) {
                        setClipFullModalState({
                          isOpen: ModalType.Clip,
                          shareId: clip?.shareId,
                          contextOverride: {
                            type: "match",
                            metadata: {
                              source: "modal",
                            },
                          },
                        });
                      }
                    }}
                  >
                    {clip.title}
                  </StyledClipTitle>
                )}
                {isPruned ? (
                  <PrunedText variant={"text.labelXSmall"} color="chalk" mb={3}>
                    Sorry, this clip was archived because no one was watching
                    it. You can create it again at any time.
                  </PrunedText>
                ) : (
                  <Flex
                    justifyContent="flex-start"
                    style={{ gap: 4 }}
                    flexGrow="1"
                    alignItems={"center"}
                  >
                    <StyledMdOutlineTimer />
                    <Text variant={"text.labelXSmall"} color="chalk">
                      {moment.timelineLabel}
                    </Text>
                  </Flex>
                )}
                <MomentTags tags={moment.displayTags} clipId={clip.shareId} />
              </Flex>
            </Flex>
          </Flex>
        </Flex>

        {!outdated && (
          <>
            {" "}
            {!isPruned ? (
              <RemixButton
                size="tiny"
                classification="roundedFilled"
                flex={true}
                style={{ minWidth: 100, maxWidth: 100 }}
                onClick={() => {
                  setQuickEditPortalOpen(true);
                  analytics.track("Match History League - Remix Button Hit", {
                    userGroup: allstarUser?.user?.userGroup?.type,
                  });
                }}
              >
                <StyledRemixIcon />
                Remix
              </RemixButton>
            ) : (
              <>
                {processing ? (
                  <>
                    <StyledProcessingButton
                      flex={true}
                      size="tiny"
                      flexShrink={0}
                      maxWidth={150}
                      onClick={() => setPortalOpen(true)}
                    >
                      Clip Pending
                      <Lottie
                        animationData={Remixing}
                        loop={true}
                        style={{ height: 36, width: 36, minWidth: 36 }}
                      />
                    </StyledProcessingButton>
                  </>
                ) : (
                  <>
                    <ClipButton
                      size="tiny"
                      classification="roundedFilled"
                      flex={true}
                      onClick={async () => handleClick()}
                      onClickCapture={() => {}}
                      maxWidth={100}
                    >
                      {processing ? (
                        <Lottie
                          animationData={Loading}
                          style={{ height: 24, width: 24 }}
                          loop={true}
                        />
                      ) : (
                        <>
                          <StyledMdOutlineMovieCreation />
                          Restore
                        </>
                      )}
                    </ClipButton>
                  </>
                )}
              </>
            )}
          </>
        )}
      </Flex>
    </>
  );
};

const StyledProcessingButton = styled(ProcessingButton)`
  font-size: ${({ theme }) => theme.fontSizes[1]};
`;

const StyledMdOutlineMovieCreation = styled(MdOutlineMovieCreation)`
  color: ${({ theme }) => theme.colors.ice};
  height: 20px;
  margin-right: 6px;
  min-width: 16px;
  width: 20px;
`;

const ClipButton = styled(Button)`
  font-size: ${({ theme }) => theme.fontSizes[1]};
  font-weight: 700;
  padding: 16px;

  :disabled {
    cursor: not-allowed;
  }

  :disabled svg {
    color: ${({ theme }) => theme.colors.chalkAlpha40};
  }
`;
const PrunedText = styled(Text)`
  max-width: 250px;
  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    max-width: 100%;
  }
`;

const DarkOverlay = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 8px;
  z-index: 1;
`;
const StyledErrorIcon = styled(BiErrorCircle)`
  color: ${({ theme }) => theme.colors.error};
  width: 48px;
  height: 48px;
  z-index: 2;
`;

const StyledClipTitle = styled(Text)`
  transition: color 0.2s;
  cursor: pointer;
  &:hover {
    color: ${({ theme }) => theme.colors.envy};
  }
`;

const StyledMdOutlineTimer = styled(MdOutlineTimer)`
  color: ${({ theme }) => theme.colors.envy};
  height: 15px;
  width: 15px;
  margin-top: 1px;
`;
const RemixButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.midnight};
  background-image: linear-gradient(
    225deg,
    rgba(249, 83, 112, 0.4) 0%,
    rgba(114, 67, 154, 0.4) 100%
  );
  font-size: ${({ theme }) => theme.fontSizes[1]};
  font-weight: 700;
  padding: 16px;

  :hover {
    background-color: ${({ theme }) => theme.colors.darkerNed};
  }
`;

const StyledRemixIcon = styled(RemixIcon)`
  height: 20px;
  margin-right: 6px;
  width: 20px;
`;
const PrunedThumbnail = styled(Flex)`
  background-size: cover;
  cursor: pointer;
  justify-content: center;
  align-items: center;
  height: 108px;
  position: relative;
  width: 192px;
  border-radius: 8px;
  flex-shrink: 0;
  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    flex-grow: 1;
    width: 100%;
    aspect-ratio: 16/9;
    height: inherit;
  }
`;

export { MomentClip };
